import React, { useMemo, useContext } from 'react';
import { bool, node, func, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import YouTube from 'react-youtube';
import cx from 'classnames';

import useStyles from 'hooks/useStyles';
import CourseContext from 'routes/course/components/CourseContext';
import { getButtonText } from 'routes/course/components/ContinueLearningCTA/utils';

import BrightcovePlayer from 'components/BrightcovePlayer';
import Button from 'components/Button';

import { YOUTUBE } from 'constants/videoSource';
import ApplicationContext from 'components/ApplicationContext';

import styles from './VideoPlayList.scss';
import CourseCover from '../CourseCover';

function VideoPlayList({
  cta,
  isLearningPage,
  currentChapterName,
  goCourseLearningPage,
}) {
  useStyles(styles);

  const { t } = useTranslation(['course']);

  const {
    screenWidth: { isMobile, isTablet },
  } = useContext(ApplicationContext);

  const isTabletOrMobile = isMobile || isTablet;

  const {
    nextVideoTitle,
    updateLessonProgress,
    onEndedCurrentLesson,
    pauseCurrentLesson,
    playCurrentLesson,
    playerRef,
    playNextLesson,
    currentLesson,
    showAutoPlayController,
    youtubeRef,
    userPurchased,
    isPreOrdered,
    isLessonListOpen,
    learningProgress,
    onLessonListPopupModeOpen,
    onLoadedLesson,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
  } = useContext(CourseContext);

  const showContinueLearningButton =
    !isLearningPage && (userPurchased || isPreOrdered);
  const showLessonListButton = !isLearningPage && !isLessonListOpen;

  const handleContinueLearningButtonClick = () => {
    if (typeof goCourseLearningPage === 'function') {
      goCourseLearningPage();
    }
  };

  const renderLessonPlayer = useMemo(() => {
    if (currentLesson.isLocked || !currentLesson.videoId) {
      return <CourseCover />;
    }

    if (!currentLesson.videoIsReady) {
      return null;
    }

    if (currentLesson.videoSource === YOUTUBE) {
      const youtubeOptions = {
        height: '100%',
        width: '100%',
        playerVars: {
          // https://developers.google.com/youtube/player_parameters
          autoplay: 1,
          mute: 1,
          modestbranding: 1,
        },
      };

      return (
        <YouTube
          ref={youtubeRef}
          videoId={currentLesson.videoId}
          containerClassName={styles.player}
          opts={youtubeOptions}
          onPause={pauseCurrentLesson}
          onPlay={playCurrentLesson}
          onEnd={onEndedCurrentLesson}
        />
      );
    }

    return (
      <BrightcovePlayer
        caption={currentChapterName}
        className={styles.player}
        ref={playerRef}
        videoId={currentLesson.videoId}
        onPlay={playCurrentLesson}
        onPause={pauseCurrentLesson}
        onEnd={onEndedCurrentLesson}
        onSeek={updateLessonProgress}
        onLeave={updateLessonProgress}
        onPlaying={updateLessonProgress}
        initialSeconds={currentLesson.initialSeconds}
        showAutoPlayController={showAutoPlayController}
        nextVideoTitle={nextVideoTitle}
        playNextVideo={playNextLesson}
        onLoaded={onLoadedLesson}
        onProgressDragStart={onProgressDragStart}
        onProgressDragEnd={onProgressDragEnd}
        onForward={onForwardStart}
        onBackward={onRewindStart}
      />
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cta,
    currentLesson.isLocked,
    currentLesson.progressDuration,
    currentLesson.videoId,
    currentLesson.videoIsReady,
    currentLesson.videoOtp,
    currentLesson.videoPlaybackInfo,
    currentLesson.videoSource,
    nextVideoTitle,
    updateLessonProgress,
    onEndedCurrentLesson,
    pauseCurrentLesson,
    playCurrentLesson,
    playerRef,
    playNextLesson,
    currentChapterName,
    showAutoPlayController,
    t,
    youtubeRef,
  ]);

  return (
    <>
      <div id="videoWrapper" className={styles.videoWrapper}>
        <div className={styles.videoPlayer}>{renderLessonPlayer}</div>
        {isTabletOrMobile && (
          <div className={styles.playlistWrapperOuter}>
            {showContinueLearningButton && (
              <Button
                disabled={isPreOrdered}
                className={cx(styles.button, styles.continueLearningButton)}
                block
                onClick={handleContinueLearningButtonClick}
                text={t(`videoplayer.watch.${getButtonText(learningProgress)}`)}
              />
            )}
            {showLessonListButton && (
              <Button
                className={styles.button}
                block
                type="dark"
                onClick={onLessonListPopupModeOpen}
                text={t(`videoplayer.label.lessons`)}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

VideoPlayList.propTypes = {
  cta: node,
  isLearningPage: bool,
  goCourseLearningPage: func,
  currentChapterName: string,
};

VideoPlayList.defaultProps = {
  cta: undefined,
  goCourseLearningPage: undefined,
  isLearningPage: false,
  currentChapterName: '',
};

export default VideoPlayList;
